import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isAfter } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { GenericFallbacksWrapper } from '../../../../../components/Fallback/GenericFallbacksWrapper';
import { usePreviousValue } from '../../../../../hooks/usePreviousValue';
import { Commentary, NewCommentaryId } from '../../../../../schemas/Commentary.schema';
import { commentariesByCompanyIdState } from '../../../../../services/state/CommentariesState';
import { cardStyles } from '../../../../../theme/component-styles';
import { plainDateToZonedDateTime } from '../../../../../util/date-utilts';
import { PaneContents, PanelContainer, PanelPagination } from '../../../../CompanyProfiles2/Summary/Panel';
import { isDrawerOpenState, selectedCommentaryStateJ } from '../../../state/UIStateJ';
import { SubSection } from '../../../Summary/components/SubSection';
import { NoDataMessage2 } from '../../Messages/HelpMessage';
import { CommentarySummaryCard } from './CommentarySummaryCard';
import { useGetTodaysCommentary } from './useCommentaryActions';

const CommentariesPanelHeader = styled('div')`
  align-items: center;
  column-gap: 1rem;
  display: grid;
  grid-template-areas: 'title fund-selector spacer pages';
  grid-template-columns: max-content minmax(12.5rem, 20rem) auto max-content;
`;

export function RecentCommentariesPanel(props: { companyId: number }) {
  const { companyId } = props;
  const setSelectedCommentary = useSetAtom(selectedCommentaryStateJ);
  const setOpenDrawer = useSetAtom(isDrawerOpenState);
  const commentaries = useAtomValue(commentariesByCompanyIdState(companyId));
  const [selectedSummary, setSelectedSummary] = useState(commentaries?.at(0));

  const sortedCommentaries = useMemo(() => {
    return getSortedActivities(commentaries ?? []);
  }, [commentaries]);
  const todaysCommentary = useGetTodaysCommentary(companyId);
  const previousCommentaries = usePreviousValue(sortedCommentaries);

  const onPageChange = useCallback(
    (pageId: number) => {
      setSelectedSummary(sortedCommentaries[pageId - 1]);
    },
    [sortedCommentaries, setSelectedSummary]
  );

  const onEditClick = useCallback(() => {
    setSelectedCommentary(selectedSummary);
    setOpenDrawer(true);
  }, [selectedSummary, setOpenDrawer, setSelectedCommentary]);

  const onCreateClick = useCallback(() => {
    setSelectedCommentary(todaysCommentary);
    setOpenDrawer(true);
  }, [setOpenDrawer, setSelectedCommentary, todaysCommentary]);

  useEffect(() => {
    if (previousCommentaries?.at(0)?.id !== sortedCommentaries.at(0)?.id) {
      const newlyAddedCommentary = sortedCommentaries[0];
      setSelectedSummary(newlyAddedCommentary);
    } else {
      const selectedIndex = sortedCommentaries.findIndex((com) => com.id === selectedSummary?.id);
      const updatedCommentary = sortedCommentaries[selectedIndex];
      setSelectedSummary(updatedCommentary);
    }
  }, [previousCommentaries, selectedSummary?.id, sortedCommentaries]);

  return (
    <GenericFallbacksWrapper>
      <SubSection
        title={'Recent Performance Commentaries'}
        collapseProps={{ collapsible: true, defaultExpanded: true }}
        noCard
        actionComponent={
          todaysCommentary.id === NewCommentaryId ? (
            <Button
              color={'secondary'}
              variant={'outlined'}
              onClick={onCreateClick}
              endIcon={<AddIcon />}
              sx={{ marginLeft: 'auto' }}
            >
              Add Performance Commentary
            </Button>
          ) : (
            <Button
              color={'secondary'}
              variant={'outlined'}
              onClick={onCreateClick}
              sx={{ marginLeft: 'auto' }}
            >
              {`Edit Today's Commentary`}
            </Button>
          )
        }
      >
        <PanelContainer style={{ ...cardStyles }}>
          <CommentariesPanelHeader>
            <PanelPagination
              key={`panel-${sortedCommentaries.at(0)?.id ?? -1}`}
              pageCount={Math.min(3, sortedCommentaries.length)}
              onChange={onPageChange}
            />
          </CommentariesPanelHeader>
          <PaneContents>
            {selectedSummary ? (
              <CommentarySummaryCard
                key={`preview-${selectedSummary.updatedAt}`}
                commentary={selectedSummary}
                onEditClick={onEditClick}
              />
            ) : (
              <NoDataMessage2 />
            )}
          </PaneContents>
        </PanelContainer>
      </SubSection>
    </GenericFallbacksWrapper>
  );
}

function getSortedActivities(commentaries: Commentary[]) {
  return [...commentaries].sort((com1, com2) => {
    return isAfter(plainDateToZonedDateTime(com1.date), plainDateToZonedDateTime(com2.date)) ? -1 : 1;
  });
}
